import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X, Check, AlertCircle, Play, Trash2 } from 'lucide-react';

const API_BASE = '/api';
const MAX_CONCURRENT_UPLOADS = 3;
const MAX_CONCURRENT_PROCESSING = 2;

export function VideoUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]); // { id, file, status, progress, video_id, public_url, error, processing }
  const xhrRefs = useRef({}); // Store XHR instances by file ID
  const [processedVideos, setProcessedVideos] = useState([]); // Videos ready for publishing
  const [selectedVideos, setSelectedVideos] = useState([]); // Selected video IDs for publishing
  const [postTexts, setPostTexts] = useState({}); // Editable post texts { video_id: "text..." }
  const [scheduleTimes, setScheduleTimes] = useState({}); // Schedule times { video_id: "2025-11-02T14:30" }
  const pollingInterval = useRef(null);

  // Poll for processed videos every 10 seconds
  useEffect(() => {
    const fetchProcessedVideos = async () => {
      try {
        const response = await fetch(`${API_BASE}/get-processed-videos.php`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success && data.videos) {
          setProcessedVideos(data.videos);
          // Initialize postTexts with videos that don't have edits yet
          setPostTexts(prev => {
            const newTexts = { ...prev };
            data.videos.forEach(video => {
              if (!newTexts[video.video_id] && video.post_text) {
                newTexts[video.video_id] = video.post_text;
              }
            });
            return newTexts;
          });
          // Initialize scheduleTimes with default (now + 1 hour)
          setScheduleTimes(prev => {
            const newTimes = { ...prev };
            data.videos.forEach(video => {
              if (!newTimes[video.video_id]) {
                const defaultTime = new Date();
                defaultTime.setHours(defaultTime.getHours() + 1);
                newTimes[video.video_id] = defaultTime.toISOString().slice(0, 16);
              }
            });
            return newTimes;
          });
        }
      } catch (error) {
        console.error('Error fetching processed videos:', error);
      }
    };

    // Initial fetch
    fetchProcessedVideos();

    // Set up polling
    pollingInterval.current = setInterval(fetchProcessedVideos, 10000);

    // Cleanup
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type === 'video/mp4'
    );

    if (droppedFiles.length === 0) {
      toast.error('Tylko pliki MP4 są akceptowane');
      return;
    }

    addFilesToQueue(droppedFiles);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFilesToQueue(selectedFiles);
    e.target.value = ''; // Reset input
  };

  const addFilesToQueue = (newFiles) => {
    const fileEntries = newFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      status: 'pending', // pending, uploading, processing, ready, error
      progress: 0,
      s3_key: null,
      error: null
    }));

    setFiles(prev => [...prev, ...fileEntries]);

    // Start uploading
    setTimeout(() => {
      uploadNextBatch();
    }, 100);
  };

  const uploadNextBatch = () => {
    setFiles(currentFiles => {
      const uploading = currentFiles.filter(f => f.status === 'uploading').length;
      const pending = currentFiles.filter(f => f.status === 'pending');

      const slotsAvailable = MAX_CONCURRENT_UPLOADS - uploading;
      const toUpload = pending.slice(0, slotsAvailable);

      // Pass the entire file entry, not just ID
      toUpload.forEach(fileEntry => {
        uploadFile(fileEntry.id, fileEntry.file, fileEntry);
      });

      return currentFiles;
    });
  };

  const uploadFile = async (fileId, file, entry) => {
    // Update status to uploading
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, status: 'uploading', progress: 0 } : f
    ));

    if (!file) {
      console.error('File not provided:', fileId);
      return;
    }

    const fileEntry = entry;

    try {
      console.log('Starting upload for:', file.name, 'Size:', file.size);

      const formData = new FormData();
      formData.append('video', file);

      const xhr = new XMLHttpRequest();
      xhrRefs.current[fileId] = xhr; // Store reference for cancellation

      // Progress tracking
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          console.log(`Upload progress for ${file.name}: ${percentComplete}%`);
          setFiles(prev => prev.map(f =>
            f.id === fileId ? { ...f, progress: percentComplete } : f
          ));
        }
      });

      // Completion
      xhr.addEventListener('load', () => {
        console.log('Upload complete. Status:', xhr.status, 'Response:', xhr.responseText);
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          if (response.success) {
            setFiles(prev => prev.map(f =>
              f.id === fileId ? {
                ...f,
                status: 'ready',
                progress: 100,
                video_id: response.video_id,
                public_url: response.public_url
              } : f
            ));
            toast.success(`${file.name} - upload zakończony`);
          } else {
            setFiles(prev => prev.map(f =>
              f.id === fileId ? {
                ...f,
                status: 'error',
                error: response.error || 'Upload failed'
              } : f
            ));
            toast.error(`${file.name} - błąd: ${response.error}`);
          }
        } else {
          setFiles(prev => prev.map(f =>
            f.id === fileId ? {
              ...f,
              status: 'error',
              error: `HTTP ${xhr.status}`
            } : f
          ));
          toast.error(`${file.name} - błąd HTTP ${xhr.status}`);
        }

        // Upload next file in queue
        delete xhrRefs.current[fileId]; // Clean up reference
        uploadNextBatch();
      });

      // Error handling
      xhr.addEventListener('error', () => {
        setFiles(prev => prev.map(f =>
          f.id === fileId ? {
            ...f,
            status: 'error',
            error: 'Network error'
          } : f
        ));
        toast.error(`${file.name} - błąd sieci`);
        delete xhrRefs.current[fileId]; // Clean up reference
        uploadNextBatch();
      });

      xhr.open('POST', `${API_BASE}/upload-video.php`);
      xhr.withCredentials = true;
      xhr.send(formData);

    } catch (error) {
      setFiles(prev => prev.map(f =>
        f.id === fileId ? {
          ...f,
          status: 'error',
          error: error.message
        } : f
      ));
      toast.error(`${file.name} - błąd: ${error.message}`);
      uploadNextBatch();
    }
  };

  const handleProcessAllVideos = async () => {
    const readyFiles = files.filter(f => f.status === 'ready');

    if (readyFiles.length === 0) {
      toast.error('Brak filmów gotowych do przetworzenia');
      return;
    }

    toast.success(`Rozpoczynam przetwarzanie ${readyFiles.length} filmów...`);

    // Process files in batches
    const processBatch = async (batch) => {
      const promises = batch.map(fileEntry =>
        handleProcessVideo(fileEntry.id, fileEntry.video_id, fileEntry.public_url)
      );
      await Promise.all(promises);
    };

    // Split into batches of MAX_CONCURRENT_PROCESSING
    for (let i = 0; i < readyFiles.length; i += MAX_CONCURRENT_PROCESSING) {
      const batch = readyFiles.slice(i, i + MAX_CONCURRENT_PROCESSING);
      await processBatch(batch);
    }
  };

  const handleProcessVideo = async (fileId, videoId, publicUrl) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, processing: true } : f
    ));

    try {
      const response = await fetch(`${API_BASE}/process-video.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          video_url: publicUrl,
          video_id: videoId
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Wideo wysłane do przetwarzania! 🎬');
        setFiles(prev => prev.map(f =>
          f.id === fileId ? { ...f, processing: false, status: 'processing' } : f
        ));
      } else {
        toast.error('Błąd: ' + (result.error || 'Nieznany błąd'));
        setFiles(prev => prev.map(f =>
          f.id === fileId ? { ...f, processing: false } : f
        ));
      }
    } catch (error) {
      toast.error('Błąd wysyłania: ' + error.message);
      setFiles(prev => prev.map(f =>
        f.id === fileId ? { ...f, processing: false } : f
      ));
    }
  };

  const removeFile = (fileId) => {
    // Cancel upload if in progress
    if (xhrRefs.current[fileId]) {
      xhrRefs.current[fileId].abort();
      delete xhrRefs.current[fileId];
      console.log('Upload cancelled for file:', fileId);
    }

    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleDeleteProcessedVideo = async (videoId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten film?')) return;

    try {
      const response = await fetch(`${API_BASE}/delete-processed-video.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ video_id: videoId })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Film usunięty.');
        setProcessedVideos(prev => prev.filter(v => v.video_id !== videoId));
      } else {
        toast.error('Błąd podczas usuwania filmu: ' + (result.message || 'Nieznany błąd'));
      }
    } catch (error) {
      toast.error('Błąd sieci: ' + error.message);
    }
  };
  const handlePublishToPostiz = async () => {
    if (selectedVideos.length === 0) {
      toast.error('Zaznacz przynajmniej jeden film do publikacji');
      return;
    }

    // Prepare videos with their edited post texts and schedule times
    const videosToPublish = selectedVideos.map(videoId => ({
      video_id: videoId,
      post_text: postTexts[videoId] || '',
      schedule_time: scheduleTimes[videoId] || new Date().toISOString()
    }));

    try {
      const response = await fetch(`${API_BASE}/publish-videos-postiz.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ videos: videosToPublish })
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Opublikowano ${result.published} filmów w Postiz! 🎉`);
        setSelectedVideos([]);
        // Refresh processed videos list
        const refreshResponse = await fetch(`${API_BASE}/get-processed-videos.php`, {
          credentials: 'include'
        });
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setProcessedVideos(refreshData.videos);
          // Clean up postTexts and scheduleTimes for published videos
          setPostTexts(prev => {
            const newTexts = { ...prev };
            selectedVideos.forEach(videoId => {
              delete newTexts[videoId];
            });
            return newTexts;
          });
          setScheduleTimes(prev => {
            const newTimes = { ...prev };
            selectedVideos.forEach(videoId => {
              delete newTimes[videoId];
            });
            return newTimes;
          });
        }
      } else {
        toast.error('Błąd publikacji: ' + (result.errors?.join(', ') || 'Nieznany błąd'));
      }
    } catch (error) {
      toast.error('Błąd wysyłania: ' + error.message);
    }
  };

  const toggleVideoSelection = (videoId) => {
    setSelectedVideos(prev =>
      prev.includes(videoId)
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const selectAllVideos = () => {
    if (selectedVideos.length === processedVideos.length) {
      setSelectedVideos([]);
    } else {
      setSelectedVideos(processedVideos.map(v => v.video_id));
    }
  };

  const handlePostTextChange = (videoId, text) => {
    setPostTexts(prev => ({
      ...prev,
      [videoId]: text
    }));
  };

  const handleScheduleTimeChange = (videoId, time) => {
    setScheduleTimes(prev => ({
      ...prev,
      [videoId]: time
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <Check className="w-5 h-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading':
        return 'bg-blue-100 border-blue-300';
      case 'processing':
        return 'bg-yellow-100 border-yellow-300';
      case 'ready':
        return 'bg-green-100 border-green-300';
      case 'error':
        return 'bg-red-100 border-red-300';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Oczekuje...';
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Przetwarzanie...';
      case 'ready':
        return 'Gotowe';
      case 'error':
        return 'Błąd';
      default:
        return status;
    }
  };

  return (
    <div className="container mx-auto p-4 lg:p-6 max-w-7xl">
      {/* Header */}
      <header className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
            <button onClick={() => navigate('/')} className="text-sm text-primary hover:underline mb-2 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Powrót do Dashboard
            </button>
            <h2 className="text-2xl md:text-3xl font-bold">🎥 Upload Video</h2>
            <p className="text-muted-foreground mt-1">Wgraj pliki MP4 do przetworzenia</p>
        </div>
      </header>

      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-6 md:p-12 mb-6 transition-colors ${
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 bg-gray-50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <Upload className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg md:text-xl font-semibold mb-2">
            {isDragging ? 'Upuść pliki tutaj' : 'Przeciągnij i upuść pliki MP4'}
          </h3>
          <p className="text-gray-600 mb-4">lub</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium w-full sm:w-auto"
          >
            Wybierz pliki z dysku
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <p className="text-sm text-gray-500 mt-4">
            Możesz wgrać wiele plików jednocześnie (max {MAX_CONCURRENT_UPLOADS} równolegle)
          </p>
        </div>
      </div>

      {/* Files List */}
      <>
        {files.length > 0 && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h3 className="text-lg md:text-xl font-semibold">
                Pliki ({files.length}) -
                {' '}{files.filter(f => f.status === 'uploading').length} w trakcie,
                {' '}{files.filter(f => f.status === 'ready').length} gotowe
              </h3>
              {files.filter(f => f.status === 'ready').length > 0 && (
                <button
                  onClick={handleProcessAllVideos}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <Play className="w-5 h-5" />
                  Wyślij wszystkie ({files.filter(f => f.status === 'ready').length})
                </button>
              )}
            </div>

            {files.map((fileEntry) => (
              <div
                key={fileEntry.id}
                className={`border rounded-lg p-4 transition-colors ${getStatusColor(fileEntry.status)}`}
              >
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {getStatusIcon(fileEntry.status)}
                      <h4 className="font-medium text-gray-900 truncate max-w-full">
                        {fileEntry.file.name}
                      </h4>
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        ({formatFileSize(fileEntry.file.size)})
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        {(fileEntry.status === 'uploading' || fileEntry.status === 'processing') && (
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${fileEntry.progress}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                        {getStatusText(fileEntry.status)}
                        {fileEntry.status === 'uploading' && ` (${fileEntry.progress}%)`}
                      </span>
                    </div>

                    {fileEntry.error && (
                      <p className="text-sm text-red-600 mt-2 break-words">
                        Błąd: {fileEntry.error}
                      </p>
                    )}

                    {fileEntry.public_url && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs text-gray-500 font-mono break-all">
                          URL: <a href={fileEntry.public_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{fileEntry.public_url}</a>
                        </p>
                        <button
                          onClick={() => handleProcessVideo(fileEntry.id, fileEntry.video_id, fileEntry.public_url)}
                          disabled={fileEntry.processing || fileEntry.status === 'processing'}
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          {fileEntry.processing ? '⏳ Wysyłanie...' : fileEntry.status === 'processing' ? '🎬 Przetwarzane' : '🎬 Przetwórz wideo'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="ml-auto self-start sm:self-center">
                    <button
                      onClick={() => removeFile(fileEntry.id)}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Usuń z listy"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {files.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Brak plików do wyświetlenia
          </div>
        )}

        {/* Processed Videos Section */}
        {processedVideos.length > 0 && (
          <div className="mt-12 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <h3 className="text-xl md:text-2xl font-bold">
                🎬 Przetworzone filmy ({processedVideos.length})
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <button
                  onClick={selectAllVideos}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium w-full sm:w-auto text-center"
                >
                  {selectedVideos.length === processedVideos.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                </button>
                {selectedVideos.length > 0 && (
                  <button
                    onClick={handlePublishToPostiz}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    📤 Opublikuj wybrane ({selectedVideos.length})
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processedVideos.map((video) => (
                <div
                  key={video.video_id}
                  className={`border rounded-lg overflow-hidden transition-all ${
                    selectedVideos.includes(video.video_id)
                      ? 'border-green-500 border-2 shadow-lg'
                      : 'border-gray-300'
                  }`}
                >
                  <div className="relative">
                    <video
                      src={video.processed_url}
                      controls
                      className="w-full aspect-video bg-black"
                      preload="metadata"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-2">
                      <button onClick={() => handleDeleteProcessedVideo(video.video_id)} className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <input
                        type="checkbox"
                        checked={selectedVideos.includes(video.video_id)}
                        onChange={() => toggleVideoSelection(video.video_id)}
                        className="w-6 h-6 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <p className="font-medium text-gray-900 truncate">
                      {video.original_filename || video.video_id}
                    </p>
                    <p className="text-xs text-gray-500">
                      ID: {video.video_id}
                    </p>
                    {video.processed_at && (
                      <p className="text-xs text-gray-500">
                        Przetworzono: {new Date(video.processed_at).toLocaleString('pl-PL')}
                      </p>
                    )}

                    {/* Post Text Editor */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tekst postu:
                      </label>
                      <textarea
                        value={postTexts[video.video_id] || ''}
                        onChange={(e) => handlePostTextChange(video.video_id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                        rows={4}
                        placeholder="Wpisz tekst postu..."
                      />
                      <div className="flex justify-between items-center mt-1">
                        <p className="text-xs text-gray-500">
                          {(postTexts[video.video_id] || '').length} znaków
                        </p>
                        {postTexts[video.video_id] !== video.post_text && (
                          <p className="text-xs text-blue-600">
                            Edytowano
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Schedule Time Picker */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        📅 Zaplanuj publikację:
                      </label>
                      <input
                        type="datetime-local"
                        value={scheduleTimes[video.video_id] || ''}
                        onChange={(e) => handleScheduleTimeChange(video.video_id, e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {scheduleTimes[video.video_id]
                          ? new Date(scheduleTimes[video.video_id]).toLocaleString('pl-PL')
                          : 'Wybierz datę i godzinę'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>

    </div>
  );
}
